import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bank, CaretRight } from "@phosphor-icons/react";
import api from "@/lib/api";
import { STAGE_LABELS, STAGE_COLORS } from "@/lib/constants";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";

const CAT_TONE = {
  PSU: "bg-blue-50 text-blue-800 border-blue-200",
  Private: "bg-violet-50 text-violet-800 border-violet-200",
  Cooperative: "bg-emerald-50 text-emerald-800 border-emerald-200",
  NBFC: "bg-amber-50 text-amber-800 border-amber-200",
  Unclassified: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function BankMaster() {
  const [banks, setBanks] = useState(null);
  useEffect(() => { api.get("/branches/meta/bank-master").then((r) => setBanks(r.data)).catch(() => setBanks([])); }, []);

  if (!banks) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading bank master…</div>;

  return (
    <div className="space-y-6" data-testid="bank-master-page">
      <PageHeader overline="Bank Branches" title="Bank Master" subtitle={`${banks.length} parent banks · branches grouped for a bank-level rollup`} />

      {banks.length === 0 ? (
        <EmptyState icon={Bank}>No branches added yet.</EmptyState>
      ) : (
        <div className="space-y-4">
          {banks.map((b) => (
            <Card key={b.bank_name} className="border-border overflow-hidden" data-testid={`bank-group-${b.bank_name}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 bg-primary text-white">
                <div className="flex items-center gap-3 min-w-0">
                  <Bank size={20} weight="fill" className="text-accent shrink-0" />
                  <div className="min-w-0">
                    <div className="font-display font-semibold truncate">{b.bank_name}</div>
                    <div className="text-[11px] text-white/60">{b.branches.length} branches · {b.empanelled} empanelled · {b.job_count} jobs</div>
                  </div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded border font-semibold uppercase tracking-wider bg-white/10 text-white border-white/20`}>{b.category}</span>
              </div>
              <div className="divide-y divide-border">
                {b.branches.map((br) => (
                  <Link key={br.id} to={`/app/branches/${br.id}`} className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-muted/50 transition-colors duration-150" data-testid={`bm-branch-${br.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-primary truncate">{br.branch_name}</div>
                      <div className="text-xs text-muted-foreground truncate">{br.region || br.address || "—"}{br.ifsc ? ` · ${br.ifsc}` : ""}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-[10px] px-2 py-0.5 rounded border ${STAGE_COLORS[br.stage]}`}>{STAGE_LABELS[br.stage]}</span>
                      <CaretRight size={14} className="text-muted-foreground" />
                    </div>
                  </Link>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground">Tip: set a bank's category (PSU / Private / Cooperative / NBFC) on any branch record and it rolls up here.</p>
    </div>
  );
}
